import { useState, useEffect } from 'react'
import { get } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import FormLoader from '../../../../components/loader/FormLoader'
import { Icon } from '@iconify/react'
import Pagination from '../../../../components/pagination/Pagination'
import { useNavigate } from 'react-router-dom'
import { WeightDiscrepancyInvoice, InvoicesResponse } from './types'
import CheckDiscrepancyModal from './modals/CheckDiscrepancyModal'
import RecordDiscrepancyModal from './modals/RecordDiscrepancyModal'
import BulkUploadModal from './modals/BulkUploadModal'
import './WeightDiscrepancy.scss'

function WeightDiscrepancy() {
  const { notify } = useGlobalContext()
  const navigate = useNavigate()
  const [data, setData] = useState<InvoicesResponse>({
    invoices: [],
    pagination: { total: 0, page: 1, limit: 20, pages: 0 },
  })
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    status: '',
    userModel: '',
    search: '',
  })

  // Modal states
  const [showCheckModal, setShowCheckModal] = useState(false)
  const [showRecordModal, setShowRecordModal] = useState(false)
  const [showBulkModal, setShowBulkModal] = useState(false)

  function fetchInvoices(page: number, pageSize: number) {
    setLoading(true)
    let url = `admin/weight-discrepancy/invoices?page=${page}&limit=${pageSize}`

    if (filters.status) url += `&status=${filters.status}`
    if (filters.userModel) url += `&userModel=${filters.userModel}`
    if (filters.search) url += `&search=${filters.search}`

    get(url)
      .then((result: any) => {
        if (result.status === 200) {
          setData(result.data)
        } else {
          notify('error', result.message)
        }
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        notify('error', 'Failed to fetch invoices')
      })
  }

  useEffect(() => {
    fetchInvoices(1, 20)
  }, [])

  const handleFilterChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSearch = () => {
    fetchInvoices(1, 20)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-NG', {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  }

  const getUserName = (invoice: WeightDiscrepancyInvoice) => {
    if (!invoice.user) return 'Unknown'
    if (invoice.userModel === 'Business') {
      return invoice.user.org_name || invoice.user.name || 'Unknown Business'
    }
    return invoice.user.firstname && invoice.user.lastname
      ? `${invoice.user.firstname} ${invoice.user.lastname}`
      : invoice.user.name || 'Unknown User'
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <span className='badge badge-default'>Draft</span>
      case 'sent':
        return <span className='badge badge-info'>Sent</span>
      case 'paid':
        return <span className='badge badge-success'>Paid</span>
      case 'overdue':
        return <span className='badge badge-danger'>Overdue</span>
      default:
        return <span className='badge badge-default'>{status}</span>
    }
  }

  const viewInvoice = (invoiceId: string) => {
    navigate(`/admin/weight-discrepancy-invoice/${invoiceId}`)
  }

  const handleModalSuccess = () => {
    fetchInvoices(1, 20)
  }

  return (
    <div className='home'>
      <div className='home_tag flex justify-between'>
        <h2>Weight Discrepancy Management</h2>
      </div>

      <div className='home_tables'>
        <div className='left'>
          {/* Action Buttons */}
          <div className='action-buttons' style={{ marginBottom: '20px' }}>
            <button
              className='btn-primary'
              onClick={() => setShowCheckModal(true)}
            >
              <Icon icon='mdi:scale-balance' />
              Check Discrepancy
            </button>
            <button
              className='btn-success'
              onClick={() => setShowRecordModal(true)}
            >
              <Icon icon='mdi:plus-circle' />
              Record Discrepancy
            </button>
            <button
              className='btn-warning'
              onClick={() => setShowBulkModal(true)}
            >
              <Icon icon='mdi:upload' />
              Bulk Upload
            </button>
          </div>

          {/* Filters */}
          <div
            className='flex justify-between items-center search-btns flex-wrap'
            style={{ gap: '10px', marginBottom: '20px' }}
          >
            <div className='flex' style={{ gap: '10px' }}>
              <select
                name='status'
                value={filters.status}
                onChange={handleFilterChange}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                }}
              >
                <option value=''>All Status</option>
                <option value='draft'>Draft</option>
                <option value='sent'>Sent</option>
                <option value='paid'>Paid</option>
                <option value='overdue'>Overdue</option>
              </select>

              <select
                name='userModel'
                value={filters.userModel}
                onChange={handleFilterChange}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                }}
              >
                <option value=''>All Types</option>
                <option value='User'>Users</option>
                <option value='Business'>Businesses</option>
              </select>
            </div>
            <div className='search flex'>
              <input
                type='search'
                name='search'
                value={filters.search}
                placeholder='Search by name, email or tracking ID'
                onChange={handleFilterChange}
              />
              <button onClick={handleSearch}>Search</button>
            </div>
          </div>

          {loading ? (
            <FormLoader position='center' />
          ) : (
            <>
              {data.invoices.length > 0 ? (
                <>
                  {/* Mobile Cards */}
                  <div className='mobile-table'>
                    {data.invoices.map(
                      (invoice: WeightDiscrepancyInvoice, i: number) => (
                        <div className='card' key={invoice._id}>
                          <div className='flex justify-between row'>
                            <div className='count flex items-center justify-center'>
                              {(data.pagination.page - 1) *
                                data.pagination.limit +
                                i +
                                1}
                            </div>
                            {getStatusBadge(invoice.status)}
                          </div>
                          <div className='row flex justify-between'>
                            <h4>Customer</h4>
                            <p>{getUserName(invoice)}</p>
                          </div>
                          <div className='row flex justify-between'>
                            <h4>Type</h4>
                            <p>{invoice.userModel}</p>
                          </div>
                          <div className='row flex justify-between'>
                            <h4>Items</h4>
                            <p>{invoice.items.length}</p>
                          </div>
                          <div className='row flex justify-between'>
                            <h4>Total Amount</h4>
                            <p>{formatCurrency(invoice.totalAmount)}</p>
                          </div>
                          <div className='row flex justify-between'>
                            <h4>Deadline</h4>
                            <p>
                              {invoice.deadline
                                ? formatDate(invoice.deadline)
                                : '-'}
                            </p>
                          </div>
                          <div className='row flex justify-between'>
                            <h4>Created</h4>
                            <p>{formatDate(invoice.createdAt)}</p>
                          </div>
                          <div className='row'>
                            <span
                              className='view'
                              onClick={() => viewInvoice(invoice._id)}
                            >
                              View Details
                            </span>
                          </div>
                        </div>
                      ),
                    )}
                  </div>

                  {/* Desktop Table */}
                  <table className='desktop-table'>
                    <thead>
                      <tr>
                        <th>S/N</th>
                        <th>Customer</th>
                        <th>Type</th>
                        <th>Items</th>
                        <th>Total Amount</th>
                        <th>Deadline</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.invoices.map(
                        (invoice: WeightDiscrepancyInvoice, i: number) => (
                          <tr key={invoice._id}>
                            <td>
                              {(data.pagination.page - 1) *
                                data.pagination.limit +
                                i +
                                1}
                            </td>
                            <td>{getUserName(invoice)}</td>
                            <td>{invoice.userModel}</td>
                            <td>{invoice.items.length}</td>
                            <td>{formatCurrency(invoice.totalAmount)}</td>
                            <td>
                              {invoice.deadline
                                ? formatDate(invoice.deadline)
                                : '-'}
                            </td>
                            <td>{getStatusBadge(invoice.status)}</td>
                            <td>{formatDate(invoice.createdAt)}</td>
                            <td>
                              <span
                                className='view'
                                onClick={() => viewInvoice(invoice._id)}
                              >
                                view
                              </span>
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>

                  <Pagination
                    currentPage={data.pagination.page}
                    totalCount={data.pagination.pages}
                    pageSize={data.pagination.limit}
                    onPageChange={(page: number, pageSize: number) => {
                      fetchInvoices(page, pageSize)
                    }}
                  />
                </>
              ) : (
                <div className='empty flex justify-center'>
                  <div className='empty-cover'>
                    <Icon icon='mdi:weight-kilogram' />
                    <h1>No Weight Discrepancy Invoices</h1>
                    <p>Record a discrepancy to create an invoice</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCheckModal && (
        <CheckDiscrepancyModal
          onClose={() => setShowCheckModal(false)}
          onSuccess={handleModalSuccess}
          onRecordDiscrepancy={() => {
            setShowCheckModal(false)
            setShowRecordModal(true)
          }}
        />
      )}

      {showRecordModal && (
        <RecordDiscrepancyModal
          onClose={() => setShowRecordModal(false)}
          onSuccess={handleModalSuccess}
        />
      )}

      {showBulkModal && (
        <BulkUploadModal
          onClose={() => setShowBulkModal(false)}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  )
}

export default WeightDiscrepancy
